/**
 * CustomerFormDialog - 고객 등록/수정 다이얼로그
 *
 * React Hook Form + Zod 스키마 기반 폼 검증
 * 섹션 기반 레이아웃으로 CRM/EMR 확장 용이
 */

import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Customer, CustomerFormData } from "@/types/customer";

// Zod 스키마
const customerSchema = z.object({
  name: z.string().min(1, "이름을 입력해주세요").max(50, "이름은 50자 이내"),
  phone: z
    .string()
    .min(1, "전화번호를 입력해주세요")
    .regex(
      /^01[0-9]-?\d{3,4}-?\d{4}$/,
      "올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)",
    ),
  birthDate: z
    .string()
    .regex(/^\d{6}$/, "YYMMDD 6자리를 입력해주세요")
    .optional()
    .or(z.literal("")),
});

type CustomerFormValues = z.infer<typeof customerSchema>;

// 전화번호 자동 포맷팅
function formatPhoneInput(value: string): string {
  const digits = value.replace(/[^0-9]/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

export interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: Customer | null;
  onSubmit: (data: CustomerFormData) => void;
  isLoading: boolean;
}

export function CustomerFormDialog({
  open,
  onOpenChange,
  customer,
  onSubmit,
  isLoading,
}: CustomerFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: "",
      phone: "",
      birthDate: "",
    },
  });

  // 추가 필드 (Zod 스키마 외)
  const [gender, setGender] = useState<string>("");
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [note, setNote] = useState("");

  // customer 변경 시 폼 리셋
  useEffect(() => {
    if (open) {
      if (customer) {
        reset({
          name: customer.name || "",
          phone: customer.phone || "",
          birthDate: customer.birthDate || "",
        });
        setGender(customer.gender || "");
        setBookingBlocked(customer.bookingBlocked || false);
        setBlockReason(customer.blockReason || "");
      } else {
        reset({ name: "", phone: "", birthDate: "" });
        setGender("");
        setBookingBlocked(false);
        setBlockReason("");
        setNote("");
      }
    }
  }, [customer, open, reset]);

  const onFormSubmit = (values: CustomerFormValues) => {
    onSubmit({
      name: values.name,
      phone: values.phone.replace(/-/g, ""),
      birthDate: values.birthDate || undefined,
      gender: gender || undefined,
      bookingBlocked,
      blockReason: bookingBlocked ? blockReason : undefined,
      note: !customer && note.trim() ? note.trim() : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] !fixed !left-[50%] !top-[50%] !translate-x-[-50%] !translate-y-[-50%] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {customer ? "고객 정보 수정" : "새 고객 등록"}
          </DialogTitle>
          <DialogDescription>
            {customer ? "고객 정보를 수정합니다." : "새로운 고객을 등록합니다."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5">
          {/* 섹션 1: 기본 정보 */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">이름 *</Label>
              <Input id="name" placeholder="고객 이름" {...register("name")} />
              {errors.name && (
                <p className="text-xs text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">전화번호 *</Label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <Input
                    id="phone"
                    placeholder="010-0000-0000"
                    maxLength={13}
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(formatPhoneInput(e.target.value))
                    }
                  />
                )}
              />
              {errors.phone && (
                <p className="text-xs text-destructive">
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthDate">생년월일 (YYMMDD)</Label>
              <Controller
                control={control}
                name="birthDate"
                render={({ field }) => (
                  <Input
                    id="birthDate"
                    placeholder="예: 901225"
                    maxLength={6}
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/[^0-9]/g, ""))
                    }
                  />
                )}
              />
              {errors.birthDate && (
                <p className="text-xs text-destructive">
                  {errors.birthDate.message}
                </p>
              )}
            </div>

            {/* 성별 */}
            <div className="space-y-2">
              <Label>성별</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger>
                  <SelectValue placeholder="선택 안함" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MALE">남성</SelectItem>
                  <SelectItem value="FEMALE">여성</SelectItem>
                  <SelectItem value="OTHER">기타</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 섹션 2: 메모 (생성 모드에서만 표시) */}
          {!customer && (
            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="note">메모 (선택)</Label>
              <Textarea
                id="note"
                placeholder="고객에 대한 메모를 입력하세요"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {/* 섹션 3: 예약 차단 (수정 모드에서만 표시) */}
          {customer && (
            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>예약 차단</Label>
                  <p className="text-xs text-muted-foreground">
                    이 고객의 예약을 차단합니다
                  </p>
                </div>
                <Switch
                  checked={bookingBlocked}
                  onCheckedChange={setBookingBlocked}
                />
              </div>
              {bookingBlocked && (
                <div className="space-y-2">
                  <Label htmlFor="blockReason">차단 사유</Label>
                  <Input
                    id="blockReason"
                    placeholder="차단 사유를 입력하세요"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              취소
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {customer ? "수정" : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
